import React, { useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Close as CloseIcon,
  WhatsApp,
  Phone,
  ContentCopy,
  Check,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export const BOOKING_PHONE = "+201041750711";
export const BOOKING_PHONE_DISPLAY = "01041750711";

/**
 * BookingContactModal - Clean, Minimalist & Elegant
 */
export default function BookingContactModal({
  open,
  onClose,
  bookingType = "room",
  itemTitle = "",
}) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard?.writeText(BOOKING_PHONE_DISPLAY);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  let title = isRtl ? "حجز غرفة" : "Book a Room";
  let message = "مرحباً، أود حجز غرفة في RED 90";

  if (bookingType === "birthday") {
    title = isRtl ? "تجهيز عيد ميلاد" : "Birthday Setup";
    message = "مرحباً، أود الاستفسار عن تجهيز عيد ميلاد في RED 90";
  } else if (bookingType === "date") {
    title = isRtl ? "تجهيز موعد (Date)" : "Date Setup";
    message = "مرحباً، أود الاستفسار عن تجهيز موعد (Date) في RED 90";
  } else if (itemTitle) {
    title = isRtl ? `حجز ${itemTitle}` : `Book ${itemTitle}`;
    message = `مرحباً، أود حجز ${itemTitle} في RED 90`;
  }

  const whatsappUrl = `https://wa.me/201041750711?text=${encodeURIComponent(message)}`;
  const phoneUrl = `tel:${BOOKING_PHONE}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: 3,
          position: "relative",
          textAlign: "center",
          direction: isRtl ? "rtl" : "ltr",
          bgcolor: "background.paper",
        },
      }}
    >
      {/* زر الإغلاق */}
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 12,
          right: isRtl ? "auto" : 12,
          left: isRtl ? 12 : "auto",
          color: "text.secondary",
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* العنوان */}
      <Typography variant="h6" sx={{ fontWeight: 800, mt: 1, mb: 1 }}>
        {title}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        {isRtl
          ? "للحجز والاستفسار، يرجى التواصل معنا عبر:"
          : "For bookings and inquiries, please contact us via:"}
      </Typography>

      {/* عرض الرقم مع زر النسخ */}
      <Box
        onClick={handleCopy}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          p: 1.5,
          mb: 2.5,
          borderRadius: 3,
          bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.05) : "#f4f5f7",
          cursor: "pointer",
          border: `1px solid ${theme.palette.divider}`,
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
          transition: "0.2s",
        }}
      >
        <Phone fontSize="small" sx={{ color: theme.palette.primary.main }} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            letterSpacing: 1,
            direction: "ltr",
          }}
        >
          {BOOKING_PHONE_DISPLAY}
        </Typography>
        <IconButton size="small" sx={{ p: 0.5, ml: 0.5 }}>
          {copied ? (
            <Check fontSize="small" color="success" />
          ) : (
            <ContentCopy fontSize="small" color="action" />
          )}
        </IconButton>
      </Box>

      {/* أزرار التواصل */}
      <Stack spacing={1.5}>
        <Button
          component="a"
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          fullWidth
          startIcon={<WhatsApp />}
          sx={{
            bgcolor: "#25D366",
            "&:hover": { bgcolor: "#1ebd5a" },
            borderRadius: 3,
            gap:1,
            py: 1.3,
            fontWeight: 800,
            fontSize: "1rem",
            color: "#fff",
            boxShadow: "none",
          }}
        >
          {isRtl ? "واتساب" : "WhatsApp"}
        </Button>

        <Button
          component="a"
          href={phoneUrl}
          variant="outlined"
          fullWidth
          startIcon={<Phone />}
          sx={{
            borderRadius: 3,
            py: 1.3,
            fontWeight: 800,
            gap:1,
            fontSize: "1rem",
            borderColor: theme.palette.divider,
            color: "text.primary",
            "&:hover": {
              borderColor: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
            },
          }}
        >
          {isRtl ? "اتصال هاتفي" : "Phone Call"}
        </Button>
      </Stack>
    </Dialog>
  );
}
